import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Link, useNavigate } from 'react-router';

import { connect, useDispatch } from 'react-redux';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image } from 'Components/Image';
import { Button } from 'Components/LegacyButton';
import ScaleLoader from 'Components/ScaleLoader';
import { breakpoints } from 'Components/ThemeProvider';
import { Typography } from 'Components/Typography';

import { productsSlugs } from 'constants/products';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import { LegacyBaseScene } from 'Scenes/LegacyBaseScene';

import stylingGelConfirmationInterstitialContent from 'assets/content/stylingGel/StylingGelConfirmationInterstitial';

import { genitive } from 'utils/textUtils';
import useMedia from 'utils/useMedia';

import * as consultationSelectors from 'dux/consultation/selectors';
import {
  getStartedFrom,
  getStylingGelHoldLevel,
  getStylingGelRecommendation,
} from 'dux/consultation/selectors';
import { getUserFirstName } from 'dux/user/selectors';
import { fetchHairProfile, fetchRecommendationBySlug } from 'dux/consultation/thunks';

const RowBox = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  margin: ${({ margin }) => margin || '0px'};
`;

const Wrapper = styled(RowBox)`
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const FlexBox = styled.div`
  max-height: 100%;
  display: flex;
  flex: ${({ flex }) => flex || '1'};
  flex-direction: column;
`;

const Container = styled.div`
  padding-top: 40px;
  width: 100%;
  ${legacyTheme.breakpoints.up('sm')} {
    padding: 0px 140px;
    height: calc(100vh - ${legacyTheme.props.headerHeight});
  }
`;

const Title = styled(Typography)`
  margin-bottom: 14px;
`;

const RecommendedTitle = styled(Typography)`
  z-index: 2;
  padding: 3px 10px 0px 10px;
`;

const RecommendedWrapper = styled.div`
  margin: 60px 0px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RecommendedSeparator = styled.div`
  position: absolute;
  width: 100%;
  height: 1px;
  background-color: ${theme.colors.tertiary['100']};
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 40px;
`;

const FirstSectionWrapper = styled(RowBox)`
  padding: 0px 18px;
`;

const ContentWrapper = styled.div`
  display: flex;
  padding: 0px 34px 60px 34px;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
`;

const StyledImage = styled.div`
  margin-right: 20px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 1em;
`;

const ImageContainer = styled(FlexBox)`
  justify-content: center;
  align-items: center;
`;

const colorMap = {
  highly_recommended: theme.colors.highlight[200],
  recommended: theme.colors.accent[200],
  not_recommended: theme.colors.secondary['100'],
};

const RecommendedSection = ({ content, recommendation }) => {
  return (
    <RecommendedWrapper>
      <RecommendedTitle style={{ backgroundColor: colorMap[recommendation] }}>
        <Typography variant="p2">{content[recommendation].title}</Typography>
      </RecommendedTitle>
      <RecommendedSeparator />
    </RecommendedWrapper>
  );
};

RecommendedSection.propTypes = {
  recommendation: PropTypes.oneOf(['highly', 'recommended', 'notRecommended']).isRequired,
  content: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string,
    })
  ).isRequired,
};

const ItemInfos = ({ imageAlt, image, title, description }) => {
  const isDesktop = useMedia(`(min-width: ${breakpoints.lg}px)`);

  return (
    <RowBox margin="0px 0px 40px 0px">
      <StyledImage>
        <Image
          alt={imageAlt}
          height={isDesktop ? '65' : '45'}
          layout="fixed"
          src={image.src}
          width={isDesktop ? '65' : '45'}
        />
      </StyledImage>
      <div>
        <Typography variant="p2">{title}</Typography>
        <Typography align="left" color="lilasDark2" variant={isDesktop ? 'p1' : 'p2'}>
          {description}
        </Typography>
      </div>
    </RowBox>
  );
};

ItemInfos.propTypes = {
  description: PropTypes.string.isRequired,
  image: srcOrNextImageProp.isRequired,
  imageAlt: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

export const StylingGelConfirmationInterstitial = ({
  answers,
  content,
  firstname,
  stylingGelHoldLevel,
  recommendation,
  startedFrom,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDesktop = useMedia(`(min-width: ${breakpoints.lg}px)`);

  useEffect(() => {
    if (!answers) {
      dispatch(fetchHairProfile());
    }
  }, [answers]);

  useEffect(() => {
    if (!recommendation) {
      dispatch(fetchRecommendationBySlug(productsSlugs.STYLING_GEL));
    }
  }, [recommendation]);

  let contextual = {
    continueLink: '/checkout/haircare',
    continueText: 'continue',
    showDoNotForget: false,
    onClick: () => {},
    backLink: null,
    backText: 'back',
  };
  if (startedFrom === 'subscription') {
    contextual = {
      continueLink: `/account/subscription/haircare?add=${productsSlugs.STYLING_GEL}`,
      continueText: 'add to my subscription',
      showDoNotForget: true,
      backOnClick: () => navigate(-1),
      backLink: '/account/subscription/haircare',
      backText: 'keep my data for later',
    };
  }

  return (
    <LegacyBaseScene header justifyContent="start">
      <Container>
        <Wrapper>
          {isDesktop && (
            <ImageContainer>
              <Image
                alt={content.imageAlt}
                height="300"
                layout="fixed"
                src={content.image.src}
                width="300"
              />
            </ImageContainer>
          )}
          <FlexBox>
            <FirstSectionWrapper>
              {!isDesktop && (
                <ImageContainer>
                  <Image
                    alt={content.imageAlt}
                    height="100"
                    layout="fixed"
                    src={content.image.src}
                    width="100"
                  />
                </ImageContainer>
              )}
              <FlexBox flex="2">
                <Title align={isDesktop ? 'center' : 'left'} variant={isDesktop ? 'h1r' : 'h3'}>
                  {content.title(genitive(firstname))}
                </Title>
                <Typography
                  align={isDesktop ? 'center' : 'left'}
                  color="lilasDark2"
                  variant={isDesktop ? 'p1' : 'p2'}
                >
                  {content.description}
                </Typography>
              </FlexBox>
            </FirstSectionWrapper>
            {Boolean(recommendation) && Boolean(stylingGelHoldLevel) ? (
              <>
                <RecommendedSection
                  content={content.recommendation}
                  recommendation={recommendation}
                />
                <ContentWrapper>
                  {content.holdLevel[stylingGelHoldLevel] && (
                    <ItemInfos
                      description={content.holdLevel[stylingGelHoldLevel]?.description}
                      image={content.holdLevel[stylingGelHoldLevel]?.image}
                      imageAlt={content.holdLevel[stylingGelHoldLevel]?.alt}
                      title={content.holdLevel[stylingGelHoldLevel]?.title}
                    />
                  )}
                  <ItemInfos
                    description={content.ingredients.description}
                    image={content.ingredients.image}
                    imageAlt={content.ingredients.alt}
                    title={content.ingredients.title}
                  />
                  <ButtonsWrapper>
                    <Button
                      Component={Link}
                      data-testid="styling-gel-confirmation-interstitial-continue"
                      to={contextual.continueLink}
                      variant="vert"
                    >
                      {contextual.continueText}
                    </Button>
                    {contextual.showDoNotForget && (
                      <Typography align="center" color="grey" variant="p2">
                        Remember to click save changes on the next screen
                      </Typography>
                    )}
                    {startedFrom === 'subscription' ? (
                      <Button Component={Link} to={contextual.backLink} variant="underlinedBlack">
                        {contextual.backText}
                      </Button>
                    ) : (
                      <Button onClick={contextual.backOnClick} variant="underlinedBlack">
                        {contextual.backText}
                      </Button>
                    )}
                  </ButtonsWrapper>
                </ContentWrapper>
              </>
            ) : (
              <LoaderContainer>
                <ScaleLoader color={theme.colors.neutral['900']} size="40px" />
              </LoaderContainer>
            )}
          </FlexBox>
        </Wrapper>
      </Container>
    </LegacyBaseScene>
  );
};

StylingGelConfirmationInterstitial.propTypes = {
  answers: PropTypes.shape({}),
  content: PropTypes.shape({
    title: PropTypes.func,
    description: PropTypes.string,
    image: srcOrNextImageProp,
    imageAlt: PropTypes.string,
    recommendation: PropTypes.shape({}),
    holdLevel: PropTypes.objectOf(
      PropTypes.shape({
        description: PropTypes.string,
        title: PropTypes.string,
        alt: PropTypes.string,
        image: srcOrNextImageProp,
      })
    ),
    ingredients: PropTypes.shape({
      description: PropTypes.string,
      title: PropTypes.string,
      alt: PropTypes.string,
      image: srcOrNextImageProp,
    }),
  }),
  firstname: PropTypes.string.isRequired,
  stylingGelHoldLevel: PropTypes.string,
  recommendation: PropTypes.oneOf(['highly_recommended', 'recommended', 'not_recommended']),
  startedFrom: PropTypes.string,
};

StylingGelConfirmationInterstitial.defaultProps = {
  answers: null,
  content: stylingGelConfirmationInterstitialContent,
  stylingGelHoldLevel: null,
  recommendation: null,
  startedFrom: '',
};

export default connect(state => ({
  firstname: getUserFirstName(state),
  answers: consultationSelectors.getConsultationAnswers(state),
  stylingGelHoldLevel: getStylingGelHoldLevel(state),
  recommendation: getStylingGelRecommendation(state),
  startedFrom: getStartedFrom(state),
}))(StylingGelConfirmationInterstitial);
